import { createClient } from '@supabase/supabase-js';

const supabaseUrl = 'https://gdspikimbmoiwdbjpcvh.supabase.co';
const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imdkc3Bpa2ltYm1vaXdkYmpwY3ZoIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Mzc1NTY5MjEsImV4cCI6MjA1MzEzMjkyMX0.gUZ43vO6mjwPcwl3ghKizE9b9oBB5r4olo0ogwHzTOA';

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Cleaner assignment functions
export const getCleanerForReservation = async (reservationId) => {
  try {
    // First, get the assignment to find the cleaner ID
    const { data: assignment, error: assignmentError } = await supabase
      .from('assigned_cleans')
      .select('cleaner_id')
      .eq('reservation_id', reservationId)
      .maybeSingle();
    
    if (assignmentError) throw assignmentError;
    
    // If no assignment exists, return null
    if (!assignment) return null;
    
    // Next, get the cleaner details using the cleaner_id
    const { data: cleaner, error: cleanerError } = await supabase
      .from('cleaners')
      .select('*')
      .eq('id', assignment.cleaner_id)
      .single();
    
    if (cleanerError) throw cleanerError;
    
    // Return data in the same format as before
    return {
      cleaner_id: assignment.cleaner_id,
      reservation_id: reservationId,
      cleaners: cleaner
    };
  } catch (error) {
    console.error('Error getting cleaner for reservation:', error.message);
    return null;
  }
};

// New function to fetch cleaner assignments for multiple reservations in one query
export const getCleanersForReservations = async (reservationIds) => {
  try {
    // Skip if no reservations provided
    if (!reservationIds || reservationIds.length === 0) {
      return {};
    }

    // Get all assignments for the provided reservation IDs in a single query
    const { data: assignments, error: assignmentsError } = await supabase
      .from('assigned_cleans')
      .select('cleaner_id, reservation_id')
      .in('reservation_id', reservationIds);
    
    if (assignmentsError) throw assignmentsError;
    
    // If no assignments exist, return empty object
    if (!assignments || assignments.length === 0) return {};
    
    // Extract unique cleaner IDs
    const cleanerIds = [...new Set(assignments.map(a => a.cleaner_id))];
    
    // Fetch all needed cleaners in a single query
    const { data: cleaners, error: cleanersError } = await supabase
      .from('cleaners')
      .select('*')
      .in('id', cleanerIds);
    
    if (cleanersError) throw cleanersError;
    
    // Create a map of cleaner data for quick lookup
    const cleanerMap = cleaners.reduce((acc, cleaner) => {
      acc[cleaner.id] = cleaner;
      return acc;
    }, {});
    
    // Create the final result mapping reservation IDs to cleaner data
    const result = {};
    
    for (const assignment of assignments) {
      const cleaner = cleanerMap[assignment.cleaner_id];
      if (cleaner) {
        result[assignment.reservation_id] = cleaner;
      }
    }
    
    return result;
  } catch (error) {
    console.error('Error getting cleaners for reservations:', error.message);
    return {};
  }
};

export const assignCleanerToReservation = async (reservationId, cleanerId) => {
  try {
    // Check if assignment already exists
    const { data: existing } = await supabase
      .from('assigned_cleans')
      .select('id')
      .eq('reservation_id', reservationId)
      .maybeSingle();
    
    if (existing) {
      // Update existing assignment
      const { data, error } = await supabase
        .from('assigned_cleans')
        .update({ cleaner_id: cleanerId })
        .eq('id', existing.id)
        .select();
      
      if (error) throw error;
      return data;
    } else {
      // Create new assignment
      const { data, error } = await supabase
        .from('assigned_cleans')
        .insert([{ 
          cleaner_id: cleanerId, 
          reservation_id: reservationId,
          assigned_date: new Date().toISOString() 
        }])
        .select();
      
      if (error) throw error;
      return data;
    }
  } catch (error) {
    console.error('Error assigning cleaner to reservation:', error.message);
    return null;
  }
};

export const removeCleanerAssignment = async (reservationId) => {
  try {
    const { error } = await supabase
      .from('assigned_cleans')
      .delete()
      .eq('reservation_id', reservationId);
    
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error removing cleaner assignment:', error.message);
    return false;
  }
};

export const getAllCleanerAssignments = async () => {
  try {
    // First, get all assignments
    const { data: assignments, error: assignmentsError } = await supabase
      .from('assigned_cleans')
      .select('*');
    
    if (assignmentsError) throw assignmentsError;
    
    // If there are no assignments, return empty array
    if (!assignments || assignments.length === 0) return [];
    
    // Create a map to store cleaner data
    const cleanerCache = new Map();
    const result = [];
    
    // Process each assignment
    for (const assignment of assignments) {
      // If we haven't cached this cleaner yet, fetch it
      if (!cleanerCache.has(assignment.cleaner_id)) {
        const { data: cleaner, error: cleanerError } = await supabase
          .from('cleaners')
          .select('*')
          .eq('id', assignment.cleaner_id)
          .single();
        
        if (cleanerError) {
          console.error('Error fetching cleaner:', cleanerError.message);
          continue; // Skip this assignment
        }
        
        // Cache the cleaner data
        cleanerCache.set(assignment.cleaner_id, cleaner);
      }
      
      // Get the cleaner from cache
      const cleaner = cleanerCache.get(assignment.cleaner_id);
      
      // Add to results
      result.push({
        ...assignment,
        cleaners: cleaner
      });
    }
    
    return result;
  } catch (error) {
    console.error('Error getting all cleaner assignments:', error.message);
    return [];
  }
};

// Function to fetch calendar pricing for multiple listings within a date range
export const getCalendarPricing = async (listingIds, startDate, endDate) => {
  try {
    // Skip if no listings provided
    if (!listingIds || listingIds.length === 0) {
      return {};
    }

    console.log(`Fetching pricing for ${listingIds.length} listings from ${startDate} to ${endDate}`);

    // Get all pricing data for the provided listing IDs and date range
    const { data: pricingData, error } = await supabase
      .from('calendar')
      .select('listing_id, date, price, status')
      .in('listing_id', listingIds)
      .gte('date', startDate)
      .lte('date', endDate);
    
    if (error) {
      console.error('Error fetching calendar pricing:', error);
      throw error;
    }
    
    // If no pricing data exists, return empty object
    if (!pricingData || pricingData.length === 0) {
      console.log('No pricing data found for the specified range and listings');
      return {};
    }
    
    console.log(`Received ${pricingData.length} pricing records`);
    
    // Create a nested map: listingId -> date -> price info
    const pricingMap = {};
    
    for (const item of pricingData) {
      if (!pricingMap[item.listing_id]) {
        pricingMap[item.listing_id] = {};
      }
      
      // Format date as string for easy lookup - ensure consistent YYYY-MM-DD format
      // Use the date-fns format function to ensure consistency with the component
      let dateObj;
      if (typeof item.date === 'string') {
        // Handle ISO string or any string format
        dateObj = new Date(item.date);
      } else {
        // Handle Date object
        dateObj = item.date;
      }
      
      // Ensure valid date before continuing
      if (isNaN(dateObj.getTime())) {
        console.warn(`Invalid date found in pricing data:`, item.date);
        continue;
      }
      
      // Format as YYYY-MM-DD using simple string operations for reliability
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      // Log a sample of the mappings for debugging
      if (Math.random() < 0.01) { // Log roughly 1% of entries to avoid console spam
        console.log(`Sample mapping: ${item.listing_id} -> ${dateStr} -> $${item.price}`);
      }
      
      pricingMap[item.listing_id][dateStr] = {
        price: item.price,
        status: item.status
      };
    }
    
    // Log the first listing's date count for verification
    for (const listingId in pricingMap) {
      const dateCount = Object.keys(pricingMap[listingId]).length;
      console.log(`Listing ${listingId} has prices for ${dateCount} dates`);
      break; // Just log the first one
    }
    
    return pricingMap;
  } catch (error) {
    console.error('Error getting calendar pricing:', error.message);
    return {};
  }
};
